import { useEffect, useState } from 'react';
import { Save, X, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import {
  reportTemplatesApi,
  parseValues,
  TEMPLATE_CATEGORIES,
  type Template,
  type TemplateItemInput,
} from '../lib/reportTemplatesApi';

interface Props {
  template: Template;
  open: boolean;
  onClose: () => void;
  onSaved: (template: Template) => void;
}

// Feld-Mapping (matched die Backend-VALID_FIELDS). Exportiert, damit ApplyTemplateDialog
// und die Vorlagen-Vorschau dieselben Labels verwenden statt eine zweite Liste zu pflegen.
export const SECTION_FIELDS: Array<{
  sectionKey: string;
  sectionLabel: string;
  fields: Array<{ key: string; label: string }>;
}> = [
  {
    sectionKey: 'analysis',
    sectionLabel: 'Analyse',
    fields: [
      { key: 'istZustand', label: 'Ist-Zustand' },
      { key: 'staerken', label: 'Stärken' },
      { key: 'schwaechen', label: 'Schwächen' },
    ],
  },
  {
    sectionKey: 'weaknesses',
    sectionLabel: 'Schwachstellen',
    fields: [
      { key: 'schwachstellen', label: 'Schwachstellen' },
      { key: 'fragestellungen', label: 'Fragestellungen' },
    ],
  },
  {
    sectionKey: 'action-plan',
    sectionLabel: 'Maßnahmen',
    fields: [{ key: 'massnahmen', label: 'Maßnahmen' }],
  },
  {
    sectionKey: 'execution',
    sectionLabel: 'Durchführung',
    fields: [
      { key: 'durchfuehrungsschritte', label: 'Durchführungsschritte' },
      { key: 'beratungsziele', label: 'Beratungsziele' },
      { key: 'vorgehensschritte', label: 'Vorgehensschritte' },
    ],
  },
];

// Eintraege sind im Bericht Objekte ({text}, {problem}, {bezeichnung}) und nur in
// alten Vorlagen reine Strings. Der Editor haelt sie UNVERAENDERT und aendert nur
// den Text — sonst gingen beim Speichern die IDs und damit die Zuordnung
// Schwachstelle → Massnahme verloren.
type Eintrag = unknown;
type ItemsMap = Record<string, Record<string, Eintrag[]>>;

export const eintragText = (e: Eintrag): string => {
  if (typeof e === 'string') return e;
  const o = (e ?? {}) as Record<string, unknown>;
  return String(o.text ?? o.problem ?? o.bezeichnung ?? '');
};

const setzeEintragText = (e: Eintrag, text: string): Eintrag => {
  if (typeof e === 'string' || e === null || typeof e !== 'object') return text;
  const o = e as Record<string, unknown>;
  if ('problem' in o) return { ...o, problem: text };
  if ('bezeichnung' in o) return { ...o, bezeichnung: text };
  return { ...o, text };
};

function buildItemsMap(template: Template): ItemsMap {
  const map: ItemsMap = {};
  for (const sec of SECTION_FIELDS) {
    map[sec.sectionKey] = {};
    for (const f of sec.fields) map[sec.sectionKey][f.key] = [];
  }
  for (const item of template.items) {
    if (!map[item.sectionKey]) map[item.sectionKey] = {};
    map[item.sectionKey][item.fieldKey] = parseValues(item);
  }
  return map;
}

function toInputItems(map: ItemsMap): TemplateItemInput[] {
  const out: TemplateItemInput[] = [];
  for (const [sectionKey, fields] of Object.entries(map)) {
    for (const [fieldKey, values] of Object.entries(fields)) {
      if (values.length > 0) {
        out.push({ sectionKey, fieldKey, values });
      }
    }
  }
  return out;
}

export default function TemplateEditModal({ template, open, onClose, onSaved }: Props) {
  const { darkMode } = useDarkMode();
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description);
  const [category, setCategory] = useState<string | null>(template.category);
  const [items, setItems] = useState<ItemsMap>(() => buildItemsMap(template));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(template.name);
      setDescription(template.description);
      setCategory(template.category);
      setItems(buildItemsMap(template));
      setError(null);
    }
  }, [open, template]);

  if (!open) return null;

  const updateBullet = (sectionKey: string, fieldKey: string, index: number, value: string) => {
    setItems((prev) => {
      const next = { ...prev, [sectionKey]: { ...prev[sectionKey] } };
      next[sectionKey][fieldKey] = [...next[sectionKey][fieldKey]];
      next[sectionKey][fieldKey][index] = setzeEintragText(next[sectionKey][fieldKey][index], value);
      return next;
    });
  };

  const addBullet = (sectionKey: string, fieldKey: string) => {
    setItems((prev) => {
      const next = { ...prev, [sectionKey]: { ...prev[sectionKey] } };
      next[sectionKey][fieldKey] = [...next[sectionKey][fieldKey], ''];
      return next;
    });
  };

  const removeBullet = (sectionKey: string, fieldKey: string, index: number) => {
    setItems((prev) => {
      const next = { ...prev, [sectionKey]: { ...prev[sectionKey] } };
      next[sectionKey][fieldKey] = next[sectionKey][fieldKey].filter((_, i) => i !== index);
      return next;
    });
  };

  const moveBullet = (sectionKey: string, fieldKey: string, index: number, dir: -1 | 1) => {
    setItems((prev) => {
      const arr = [...prev[sectionKey][fieldKey]];
      const target = index + dir;
      if (target < 0 || target >= arr.length) return prev;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      return { ...prev, [sectionKey]: { ...prev[sectionKey], [fieldKey]: arr } };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const cleanedMap: ItemsMap = {};
      for (const [sk, fields] of Object.entries(items)) {
        cleanedMap[sk] = {};
        for (const [fk, values] of Object.entries(fields)) {
          cleanedMap[sk][fk] = values.filter((v) => eintragText(v).trim().length > 0);
        }
      }
      const inputItems = toInputItems(cleanedMap);
      const updated = await reportTemplatesApi.update(template.id, {
        name: name.trim(),
        description: description.trim(),
        category,
        items: inputItems,
      });
      onSaved(updated);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const card = darkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200';
  const inputCls = darkMode
    ? 'bg-slate-700 border-slate-600 text-white'
    : 'bg-white border-slate-300';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`rounded-xl border shadow-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto ${card}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Vorlage bearbeiten</h2>
          <button onClick={onClose} className={muted} aria-label="Schließen">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="tpl-edit-name">Name</label>
            <input
              id="tpl-edit-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg text-sm ${inputCls}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="tpl-edit-desc">Beschreibung</label>
            <textarea
              id="tpl-edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className={`w-full px-3 py-2 border rounded-lg text-sm resize-y ${inputCls}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="tpl-edit-category">Kategorie</label>
            <select
              id="tpl-edit-category"
              value={category ?? ''}
              onChange={(e) => setCategory(e.target.value || null)}
              className={`w-full px-3 py-2 border rounded-lg text-sm ${inputCls}`}
            >
              <option value="">Ohne Kategorie</option>
              {TEMPLATE_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {SECTION_FIELDS.map((sec) => (
            <div key={sec.sectionKey} className={`rounded-lg border p-3 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <h3 className="font-semibold text-sm mb-2">{sec.sectionLabel}</h3>
              {sec.fields.map((f) => {
                const values = items[sec.sectionKey]?.[f.key] ?? [];
                return (
                  <div key={f.key} className="mb-3 last:mb-0">
                    <p className={`text-xs font-medium mb-1 ${muted}`}>{f.label}</p>
                    {values.length === 0 ? (
                      <p className={`text-xs italic mb-1 ${muted}`}>Keine Einträge</p>
                    ) : (
                      values.map((v, idx) => (
                        <div key={idx} className="flex gap-1 mb-1">
                          <input
                            type="text"
                            value={eintragText(v)}
                            onChange={(e) => updateBullet(sec.sectionKey, f.key, idx, e.target.value)}
                            className={`flex-1 px-2 py-1 border rounded text-sm ${inputCls}`}
                          />
                          <button
                            onClick={() => moveBullet(sec.sectionKey, f.key, idx, -1)}
                            disabled={idx === 0}
                            className={`p-1 rounded ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'} disabled:opacity-30`}
                            title="Nach oben"
                            aria-label="Nach oben verschieben"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => moveBullet(sec.sectionKey, f.key, idx, 1)}
                            disabled={idx === values.length - 1}
                            className={`p-1 rounded ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'} disabled:opacity-30`}
                            title="Nach unten"
                            aria-label="Nach unten verschieben"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => removeBullet(sec.sectionKey, f.key, idx)}
                            className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                            title="Entfernen"
                            aria-label="Eintrag entfernen"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))
                    )}
                    <button
                      onClick={() => addBullet(sec.sectionKey, f.key)}
                      className={`mt-1 flex items-center gap-1 text-xs ${muted} hover:text-blue-600`}
                    >
                      <Plus className="w-3 h-3" />
                      Eintrag hinzufügen
                    </button>
                    {sec.sectionKey === 'action-plan' && (
                      <p className={`text-xs italic mt-1 ${muted}`}>
                        Hinweis: Neu hinzugefügte Maßnahmen sind nach dem Anwenden im Bericht keiner Schwachstelle
                        zugeordnet — das muss dort manuell ergänzt werden.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {error && (
            <div className="p-2 bg-red-100 border border-red-300 text-red-700 rounded text-sm">{error}</div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className={`px-4 py-2 text-sm rounded-lg border ${darkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={saving || name.trim().length === 0}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
